import {
  Accordion,
  Badge,
  BadgeVariants,
  ButtonText
} from '@morressier/alembic';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell, faComment } from '@fortawesome/pro-regular-svg-icons';
import { useState } from 'react';
import { Flex, Text } from '@chakra-ui/react';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { useQueryClient, useQuery } from '@tanstack/react-query';

import { ISubmissionFile } from '@publishing-engine/domain/models';
import { ExtendSubmissionDeadlineModal } from '../../../decisionModals/GrantSubmissionExtensionModal';
import { EditorialRevisionContextModal } from '../../../modals/editorial/RevisonContext';
import { useSubmissionData } from 'src/contexts/SubmissionDataProvider';
import { OverwriteReviewsModal } from '../common/OverwriteReviewsModal';
import {
  fetchSubmissionReviews,
  fetchSubmissionReviewsSummary
} from 'src/api/queries';
import { AuthorCommentsModal } from '../common/AuthorCommentsModal';
import {
  CompletedReviews,
  CriticalDeadlineReviews,
  ReviewsDeadlineRequested
} from '../common/HighlightsComponents';
import { EditorialAuthorWithdrawalRequested } from '../common/WithdrawalRequested';
import AdditionalInformationWrapper from '../../AdditionalInformationWrapper';

const IconBadge = ({
  icon,
  variant
}: {
  icon: IconProp;
  variant: BadgeVariants;
}) => (
  <Badge size="context" variant={variant}>
    <Flex
      width="24px"
      height={'24px'}
      alignItems={'center'}
      justifyContent={'center'}
    >
      <FontAwesomeIcon icon={icon} />
    </Flex>
  </Badge>
);

export const EditorialRevisionAssessmentAdditionalInfo = () => {
  const {
    submissionId,
    submissionContextId,
    workflowResponse,
    fieldsResponse: { fields, organizationId }
  } = useSubmissionData();

  const deadline = workflowResponse.workflowData.currentDeadline;
  const [showReviewContextModal, setShowReviewContextModal] = useState(false);
  const [showGrantExtensionModal, setShowGrantExtensionModal] = useState(false);
  const [showOverwriteModal, setShowOverwriteModal] = useState(false);
  const [showAuthorCommentsModal, setShowAuthorCommentsModal] = useState(false);

  const isExtensionRequested =
    workflowResponse.workflowData.deadlineExtensions?.status === 'requested';

  const authorResponse = fields.AuthorResponse?.value as string;
  const authorAttachments = fields.AuthorResponseAttachments
    ?.value as ISubmissionFile[];

  const queryClient = useQueryClient();

  const { data: reviewsData } = useQuery({
    queryKey: ['review-list', { submissionContextId, submissionId }],
    queryFn: () => fetchSubmissionReviews(submissionContextId, submissionId)
  });

  const { data: reviewsSummary } = useQuery({
    queryKey: ['summary-count', { submissionContextId, submissionId }],
    queryFn: () =>
      fetchSubmissionReviewsSummary(submissionContextId, submissionId)
  });

  const completedReviews = reviewsData?.reviewSummaryCounts?.completed || 0;

  const criticalDeadlineReviews =
    reviewsSummary?.highlightsCounts?.approachingDeadline || 0;

  const expiredReviews = reviewsSummary?.highlightsCounts?.expired || 0;

  const deadlineExtensionRequests =
    reviewsData?.reviewSummaryCounts?.extensionRequested || 0;

  const isWithdrawalRequested =
    !!workflowResponse.workflowData.withdrawal?.requested;
  const hasHighlights =
    authorResponse ||
    criticalDeadlineReviews > 0 ||
    expiredReviews > 0 ||
    deadlineExtensionRequests > 0 ||
    isWithdrawalRequested;

  return (
    <AdditionalInformationWrapper>
      {showAuthorCommentsModal && (
        <AuthorCommentsModal
          onClose={() => setShowAuthorCommentsModal(false)}
          authorResponse={authorResponse}
          authorAttachments={authorAttachments}
          submissionContextId={submissionContextId}
        />
      )}

      {showOverwriteModal && (
        <OverwriteReviewsModal
          handleCloseModal={() => setShowOverwriteModal(false)}
          submissionId={submissionId}
          submissionContextId={submissionContextId}
          organizationId={organizationId}
          refetchSubmission={() => {
            queryClient.invalidateQueries({
              queryKey: ['submission-checklists']
            });
          }}
          defaultMinReviews={workflowResponse.workflowData.minReviews}
        />
      )}

      <Flex flexDirection="column">
        {/* ----- Highlights ----- */}
        {hasHighlights && (
          <Accordion
            chakraAccordionProps={{
              width: '100%',
              defaultIndex: 0,
              allowToggle: true
            }}
            items={[
              {
                title: (
                  <Flex alignItems={'flex-start'} gap={2}>
                    <IconBadge icon={faBell} variant="neutral" />
                    <Flex flexDirection={'column'} marginLeft={2}>
                      <Text textStyle="headingItem">Highlights</Text>
                      <Text textStyle="textContext">
                        Please review key updates and other relevant information
                        in this phase.
                      </Text>
                    </Flex>
                  </Flex>
                ),
                content: (
                  <Flex flexDirection="column" gap={2}>
                    <EditorialAuthorWithdrawalRequested />
                    {authorResponse && (
                      <>
                        <Flex
                          width="100%"
                          gap={3}
                          alignItems="center"
                          paddingTop={2}
                          paddingBottom={4}
                          borderColor="neutralPrimaryBorder"
                          borderBottomWidth={1}
                          borderStyle="solid"
                        >
                          <IconBadge icon={faComment} variant="attention" />

                          <Flex flexDirection="column" flex={1}>
                            <Flex>
                              <Text textStyle="headingItem">
                                Author Comments Available
                              </Text>
                              <ButtonText
                                marginLeft={'auto'}
                                onClick={() => setShowAuthorCommentsModal(true)}
                              >
                                View Comments
                              </ButtonText>
                            </Flex>
                          </Flex>
                        </Flex>
                      </>
                    )}

                    <CompletedReviews
                      minReviews={workflowResponse.workflowData.minReviews}
                      completedReviews={completedReviews}
                    />

                    <CriticalDeadlineReviews
                      criticalDeadlineReviews={criticalDeadlineReviews}
                      expiredReviews={expiredReviews}
                    />

                    <ReviewsDeadlineRequested
                      deadlineExtensionRequests={deadlineExtensionRequests}
                    />
                  </Flex>
                )
              }
            ]}
          />
        )}
      </Flex>

      {showReviewContextModal && (
        <EditorialRevisionContextModal
          submissionId={submissionId}
          onClose={() => setShowReviewContextModal(false)}
          deadline={deadline}
          role="editorial"
        />
      )}

      {showGrantExtensionModal && (
        <ExtendSubmissionDeadlineModal
          onClose={() => setShowGrantExtensionModal(false)}
          submissionId={submissionId}
          organizationId={organizationId}
          submissionContextId={submissionContextId}
          isExtensionRequested={isExtensionRequested}
          proposedDate={
            workflowResponse.workflowData.deadlineExtensions?.proposedDate
          }
        />
      )}
    </AdditionalInformationWrapper>
  );
};
